import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SectionMeta } from './PackingSectionCard';
import { Check, Plus, Icon } from '../../ui/Icon';
import Spinner from '../../ui/Spinner';

interface Props {
  items: { name: string; meta: SectionMeta }[];
  onAdd: (titles: string[]) => Promise<void>;
}

/**
 * Pick several, then add them.
 *
 * These chips only appear while the list is empty, so adding one used to make
 * the whole block vanish — you got exactly one suggestion out of seven, and the
 * rest you had to type. Selecting is now free and only the confirm writes.
 */
export default function PackingSuggestions({ items, onAdd }: Props) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  function toggle(name: string) {
    setSelected(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
  }

  async function add() {
    if (selected.length === 0 || saving) return;
    setSaving(true);
    try {
      // Kept in the order they are offered, not the order they were tapped:
      // the chips read as a list and the sections should come out looking like
      // one, whichever way round you picked them.
      await onAdd(items.filter(i => selected.includes(i.name)).map(i => i.name));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 p-6 mb-3">
      <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3">{t('packing.suggestedSections')}</p>
      <div className="flex flex-wrap gap-2">
        {items.map(({ name, meta }) => {
          const on = selected.includes(name);
          return (
            <button
              key={name}
              onClick={() => toggle(name)}
              aria-pressed={on}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-all cursor-pointer ${meta.bg} ${meta.color} hover:brightness-95 dark:hover:brightness-125 ${
                on ? 'ring-2 ring-offset-1 ring-current dark:ring-offset-slate-900' : ''
              }`}
            >
              <Icon as={on ? Check : meta.Icon} size={13} />
              {name}
            </button>
          );
        })}
      </div>

      {selected.length > 0 && (
        <button
          onClick={add}
          disabled={saving}
          className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-teal-500 hover:bg-teal-600 disabled:opacity-60 text-white text-sm font-semibold transition-colors cursor-pointer"
        >
          {saving ? <Spinner size="sm" /> : <Icon as={Plus} size={16} />}
          {t('packing.addSuggested', { count: selected.length })}
        </button>
      )}
    </div>
  );
}
